import { NavLink, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import {
  LayoutDashboard,
  Building2,
  Users,
  KeyRound,
  Shield,
  LogOut,
  ChevronDown,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { backofficeApi } from '@/lib/backoffice-api'
import { clearStaffAccessToken, getStaffTokenPayload } from '@/lib/backoffice-auth'
import type { StaffRole } from '@/types/backoffice'

interface MenuItem {
  href: string
  label: string
  icon: React.ElementType
  roles: StaffRole[]
}

const MENU_ITEMS: MenuItem[] = [
  { href: '/backoffice', label: 'Visão geral', icon: LayoutDashboard, roles: ['OWNER', 'STAFF'] },
  { href: '/backoffice/tenants', label: 'Transportadoras', icon: Building2, roles: ['OWNER', 'STAFF'] },
  { href: '/backoffice/users', label: 'Usuários', icon: Users, roles: ['OWNER', 'STAFF'] },
  { href: '/backoffice/reset-requests', label: 'Pedidos de reset', icon: KeyRound, roles: ['OWNER', 'STAFF'] },
  { href: '/backoffice/staff', label: 'Equipe da plataforma', icon: Shield, roles: ['OWNER'] },
]

export function BackofficeShell({ children }: { readonly children: React.ReactNode }) {
  const navigate = useNavigate()
  const payload = getStaffTokenPayload()
  const role = payload?.role
  const visible = MENU_ITEMS.filter((item) => !role || item.roles.includes(role))

  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function handleLogout() {
    try {
      await backofficeApi.post('/backoffice/auth/logout')
    } catch {
      // ignore
    }
    clearStaffAccessToken()
    navigate('/backoffice/login', { replace: true })
  }

  return (
    <div className="flex h-screen bg-background">
      <aside className="flex h-screen w-16 flex-col border-r border-border bg-sidebar lg:w-64">
        <div className="flex h-16 items-center justify-center border-b border-border px-4 lg:justify-start">
          <span className="hidden text-base font-semibold text-foreground lg:block">
            TransportPro · Backoffice
          </span>
          <span className="text-base font-bold text-primary lg:hidden">BO</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-2">
            {visible.map((item) => (
              <li key={item.href}>
                <NavLink
                  to={item.href}
                  end={item.href === '/backoffice'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors',
                      'justify-center lg:justify-start',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                  title={item.label}
                >
                  <item.icon size={18} className="shrink-0" />
                  <span className="hidden lg:block">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-6">
          <p className="text-sm font-semibold text-foreground">Console da Plataforma</p>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                open && 'bg-accent text-accent-foreground',
              )}
            >
              <User size={16} className="shrink-0" />
              <span className="hidden sm:block">{payload?.name ?? '—'}</span>
              <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
            </button>
            {open && (
              <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-background shadow-md">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10 rounded-lg"
                >
                  <LogOut size={15} />
                  Sair
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  )
}
